import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { Subscription } from 'rxjs';
import { SharedModule } from 'src/app/shared/shared.module';
import { DatePickerComponent } from '../../shared/components/date-picker/date-picker.component';
import { SpinnerService } from '../../shared/services/spinner.service';
import Swal from 'sweetalert2';

@Component({
  standalone: true,
  selector: 'app-submit-accounts',
  templateUrl: './submit-accounts.component.html',
  styleUrls: ['./submit-accounts.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    BsDatepickerModule,
    DatePickerComponent,
  ],
})
export class SubmitAccountsComponent implements OnInit, OnDestroy {
  previewForm: FormGroup;
  previewForm_2: FormGroup;
  @Output() closeDialog = new EventEmitter();
  private autoTotalSubscriptions: Subscription[] = [];
  dataArray = [
    { bank: 100, receivables: 200, inventory: 300, totalEquity: 5000 },
    { receivables: 200, totalEquity: 10000 },
    { inventory: 300, totalEquity: 20000 },
  ];
  #spinnerService = inject(SpinnerService);

  currentPage = 0;
  totalPages = this.dataArray.length;

  constructor(
    private formBuilder: FormBuilder,
    public activeModal: NgbActiveModal,
  ) {}

  ngOnInit(): void {
    this.initForms(this.dataArray[this.currentPage]);
    this.initForms_Second();
    this.loadPage(0);
  }

  loadPage(index: number): void {
    if (index < 0 || index >= this.dataArray.length) {
      return;
    }
    this.dataArray[this.currentPage] = this.previewForm.value;
    this.currentPage = index;
    const data = this.dataArray[this.currentPage];
    this.initForms(data);
    this.unsubscribeAutoTotals();
    this.setupCalculations();
    setTimeout(() => this.triggerAllCalculations(), 0);
    setTimeout(() => this.calculateNetAssetTotal(), 0);
  }

  private initForms(data?: any): void {
    this.previewForm = this.formBuilder.group({
      bank: [data?.bank ?? null],
      receivables: [data?.receivables ?? null],
      inventory: [data?.inventory ?? null],
      assetOther: [data?.assetOther ?? null],
      totalCurrentAssets: [data?.totalCurrentAssets ?? null],
      totalFixedAssets: [data?.totalFixedAssets ?? null],
      totalAssets: [data?.totalAssets ?? null],
      payables: [data?.payables ?? null],
      loans: [data?.loans ?? null],
      leases: [data?.leases ?? null],
      vat: [data?.vat ?? null],
      paye: [data?.paye ?? null],
      liabilitiesOther: [data?.liabilitiesOther ?? null],
      currentLiabilities: [data?.currentLiabilities ?? null],
      nonCurrentLiabilities: [data?.nonCurrentLiabilities ?? null],
      totalLiabilities: [data?.totalLiabilities ?? null],
      netAssets: [data?.netAssets ?? null],
      retainedEarnings: [data?.retainedEarnings ?? null],
      totalEquity: [data?.totalEquity ?? null],
    });
  }

  initForms_Second() {
    this.previewForm_2 = this.formBuilder.group({
      income: [null],
      //Variables Costs
      materials: [null],
      labour: [null],
      variablesOther: [null],
      variablesTotal: [null],
      //Fixed Costs
      overhead: [null],
      salaries: [null],
      interest: [null],
      tax: [null],
      fixedOther: [null],
      fixedTotal: [null],
      profit: [null],
      issuedDate: [null],
    });
  }

  private triggerAllCalculations(): void {
    this.calculateTotal(
      ['bank', 'receivables', 'inventory', 'assetOther'],
      'totalCurrentAssets',
      this.previewForm,
    );
    this.calculateTotal(
      ['bank', 'receivables', 'inventory', 'assetOther', 'totalFixedAssets'],
      'totalAssets',
      this.previewForm,
    );
    this.calculateTotal(
      ['totalCurrentAssets', 'totalFixedAssets'],
      'totalAssets',
      this.previewForm,
    );
    this.calculateTotal(
      ['payables', 'loans', 'leases', 'vat', 'paye', 'liabilitiesOther'],
      'currentLiabilities',
      this.previewForm,
    );
    this.calculateTotal(
      [
        'payables',
        'loans',
        'leases',
        'vat',
        'paye',
        'liabilitiesOther',
        'nonCurrentLiabilities',
      ],
      'totalLiabilities',
      this.previewForm,
    );
    this.calculateTotal(
      ['currentLiabilities', 'nonCurrentLiabilities'],
      'totalLiabilities',
      this.previewForm,
    );
    this.calculateTotal(
      ['totalAssets', 'totalLiabilities'],
      'netAssets',
      this.previewForm,
      true,
    );
  }

  private setupCalculations(): void {
    this.setUpAutoTotal(
      ['bank', 'receivables', 'inventory', 'assetOther'],
      'totalCurrentAssets',
      this.previewForm,
    );
    this.setUpAutoTotal(
      ['bank', 'receivables', 'inventory', 'assetOther', 'totalFixedAssets'],
      'totalAssets',
      this.previewForm,
    );
    this.setUpAutoTotal(
      ['totalCurrentAssets', 'totalFixedAssets'],
      'totalAssets',
      this.previewForm,
    );
    this.setUpAutoTotal(
      ['payables', 'loans', 'leases', 'vat', 'paye', 'liabilitiesOther'],
      'currentLiabilities',
      this.previewForm,
    );
    this.setUpAutoTotal(
      [
        'payables',
        'loans',
        'leases',
        'vat',
        'paye',
        'liabilitiesOther',
        'nonCurrentLiabilities',
      ],
      'totalLiabilities',
      this.previewForm,
    );
    this.setUpAutoTotal(
      ['currentLiabilities', 'nonCurrentLiabilities'],
      'totalLiabilities',
      this.previewForm,
    );
    this.setUpAutoTotal(
      ['totalAssets', 'totalLiabilities'],
      'netAssets',
      this.previewForm,
      true,
    );
    this.setUpAutoTotal(
      ['materials', 'labour', 'variablesOther'],
      'variablesTotal',
      this.previewForm_2,
    );
    this.setUpAutoTotal(
      ['overhead', 'salaries', 'interest', 'tax', 'fixedOther'],
      'fixedTotal',
      this.previewForm_2,
    );
    this.setUpAutoTotal(
      ['income', 'variablesTotal', 'fixedTotal'],
      'profit',
      this.previewForm_2,
      false,
      true,
    );
  }

  private setUpAutoTotal(
    fields: string[],
    targetField: string,
    previewForm: FormGroup,
    skipNetAssetsUpdate = false,
    isProfitCalc = false,
  ): void {
    fields.forEach((field) => {
      const control = previewForm.get(field);
      if (control) {
        const sub = control.valueChanges.subscribe(() => {
          this.calculateTotal(
            fields,
            targetField,
            previewForm,
            skipNetAssetsUpdate,
            isProfitCalc,
          );
        });
        this.autoTotalSubscriptions.push(sub);
      }
    });
  }

  private calculateTotal(
    fields: string[],
    targetField: string,
    previewForm: FormGroup,
    skipNetAssetsUpdate = false,
    isProfitCalc = false,
  ): void {
    const total = fields.reduce(
      (sum, field) => sum + (+previewForm.get(field)?.value || 0),
      0,
    );
    if (isProfitCalc) {
      const income = +previewForm.get('income')?.value || 0;
      const result = income - total;
      previewForm.get(targetField)?.setValue(result, { emitEvent: false });
      return;
    }

    previewForm.get(targetField)?.setValue(total, { emitEvent: false });

    if (previewForm === this.previewForm && !skipNetAssetsUpdate) {
      this.calculateNetAssetTotal();
    }
  }

  private calculateNetAssetTotal(): void {
    const totalFixedAssets = +this.previewForm.get('totalAssets')?.value || 0;
    const nonCurrentLiabilities =
      +this.previewForm.get('totalLiabilities')?.value || 0;

    if (totalFixedAssets && nonCurrentLiabilities) {
      const netAssets = totalFixedAssets - nonCurrentLiabilities;

      this.previewForm
        .get('netAssets')
        ?.setValue(netAssets, { emitEvent: false });
    } else {
      this.previewForm.get('netAssets')?.setValue(0, { emitEvent: false });
    }
  }

  goToPrevious(): void {
    if (this.currentPage > 0) {
      this.saveCurrentPageData();
      this.currentPage--;
      this.initForms(this.dataArray[this.currentPage]);
    }
  }

  goToNext(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.saveCurrentPageData();
      this.currentPage++;
      this.initForms(this.dataArray[this.currentPage]);
    }
  }

  saveCurrentPageData(): void {
    const currentData = this.previewForm.getRawValue();
    this.dataArray[this.currentPage] = currentData;
  }

  private unsubscribeAutoTotals(): void {
    this.autoTotalSubscriptions.forEach((sub) => sub.unsubscribe());
    this.autoTotalSubscriptions = [];
  }

  handleProcess() {
    this.#spinnerService.setShowSpinner(true);
    this.dataArray[this.currentPage] = this.previewForm.value;
    const allPagesData = [...this.dataArray];
    setTimeout(() => {
      this.closeProfitDialog();
    }, 1000);
  }

  closeProfitDialog(): void {
    this.#spinnerService.setShowSpinner(false);
    this.activeModal.dismiss();
    Swal.fire({
      icon: 'success',
      text: 'Account successfully submitted.',
      showConfirmButton: true,
      confirmButtonText: 'Ok',
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeAutoTotals();
  }
}
